import React from "react";
import {
  Phone,
  Mail,
  Trash2,
  Image as ImageIcon,
  Loader2,
  Eye,
  Check,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { STATUS_OPTIONS, STATUS_STYLES, fmtDate } from "../../lib/quoteStatus";

export const QuoteTable = ({ quotes, loading, onView, onDelete, onStatus }) => (
  <div className="bg-white border-2 border-navy overflow-x-auto">
    {loading ? (
      <div className="p-16 flex items-center justify-center text-navy/60">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    ) : quotes.length === 0 ? (
      <div className="p-16 text-center text-navy/60">
        <p className="font-display uppercase text-xl text-navy">
          No quotes match this filter
        </p>
        <p className="mt-2 text-sm">
          New requests will appear here automatically.
        </p>
      </div>
    ) : (
      <table className="min-w-full" data-testid="admin-quotes-table">
        <thead>
          <tr className="bg-navy text-white font-body text-[11px] uppercase tracking-[0.15em]">
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Contact</th>
            <th className="text-left px-4 py-3">Service</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {quotes.map((q) => (
            <QuoteRow
              key={q.id}
              q={q}
              onView={onView}
              onDelete={onDelete}
              onStatus={onStatus}
            />
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const QuoteRow = ({ q, onView, onDelete, onStatus }) => (
  <tr
    data-testid={`quote-row-${q.id}`}
    className="border-t border-navy/15 hover:bg-offwhite"
  >
    <td className="px-4 py-4 text-sm text-navy/80 whitespace-nowrap">
      {fmtDate(q.created_at)}
    </td>
    <td className="px-4 py-4">
      <button
        onClick={() => onView(q.id)}
        className="font-display uppercase text-navy hover:text-gold tracking-wide text-left"
      >
        {q.name}
      </button>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {q.has_photo && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase text-navy/60">
            <ImageIcon className="h-3 w-3" /> photo
          </span>
        )}
        {(q.preferred_date || q.preferred_window) && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase text-gold font-bold">
            ★ {q.preferred_date || "any day"} · {q.preferred_window || "any time"}
          </span>
        )}
        {q.sms_consent ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase text-emerald-700 font-bold">
            <Check className="h-3 w-3" /> SMS ok
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase text-red-600 font-bold">
            No SMS
          </span>
        )}
      </div>
    </td>
    <td className="px-4 py-4 text-sm">
      <a
        href={`tel:${q.phone}`}
        className="inline-flex items-center gap-1 text-navy hover:text-gold"
      >
        <Phone className="h-3.5 w-3.5" /> {q.phone}
      </a>
      <br />
      <a
        href={`mailto:${q.email}`}
        className="inline-flex items-center gap-1 text-navy/70 hover:text-gold"
      >
        <Mail className="h-3.5 w-3.5" /> {q.email}
      </a>
    </td>
    <td className="px-4 py-4 text-sm">{q.service}</td>
    <td className="px-4 py-4">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`status-btn-${q.id}`}
            className={`font-display uppercase text-[10px] tracking-[0.15em] px-2 py-1 border-2 border-navy ${
              STATUS_STYLES[q.status || "new"]
            }`}
          >
            {q.status || "new"}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {STATUS_OPTIONS.map((s) => (
            <DropdownMenuItem
              key={s}
              onClick={() => onStatus(q.id, s)}
              className="uppercase text-xs tracking-wider"
            >
              {q.status === s && <Check className="h-3 w-3 mr-1" />}
              {s}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </td>
    <td className="px-4 py-4 text-right whitespace-nowrap">
      <button
        onClick={() => onView(q.id)}
        className="inline-flex items-center gap-1 px-2 py-1 text-navy hover:text-gold text-xs uppercase tracking-wider font-bold"
      >
        <Eye className="h-4 w-4" /> View
      </button>
      <button
        data-testid={`delete-btn-${q.id}`}
        onClick={() => onDelete(q.id)}
        className="ml-2 inline-flex items-center gap-1 px-2 py-1 text-red-600 hover:text-red-700 text-xs uppercase tracking-wider font-bold"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </td>
  </tr>
);
